import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import PropTypes from 'prop-types';
import ProductCard from 'components/ProductCard';
import { selectScreenWidth } from 'selectors/ui';
import { size } from 'components/Core/Utils/Device';
import { aggridConstants } from 'constants/Aggrid.constants';
import { analytics } from 'utils/analytics/tagmanager';
import './PromotionList.scss';
import { getPromotionFields, getValueOfAttribut } from 'helpers';
import { selectCurrentPlatform } from 'selectors/platform';
import moment from 'moment';
import { productActions } from 'actions';
import { selectItemsFromCatalog } from 'selectors/product';
import { promotionActions } from 'actions/Promotion';
import {
  selectPromotionData,
  selectPromotionSelectedProduct,
  selectPromotionsCurrentTab,
} from 'selectors/promotion';
import { pageContextActions } from 'actions/PageContext';
import { selectCurrentPageContext } from 'selectors/catalog';
import localeText from './locales';
import { platformConstants } from '../../constants';
import columnDefs from './columnDefinitions';
import { ProductCardWrapper, AgListWrapper, TabWrapper } from './PromotionList.style';

const resizeGrid = (screenWidth, columnApi, gridApi) => {
  if (columnApi && gridApi) {
    columnApi.setColumnVisible('agListNameCell', true);
    columnApi.setColumnVisible('agListReferenceCell', true);
    columnApi.setColumnVisible('agListPriceCell', true);
    columnApi.setColumnVisible('favoriteCell', true);
    columnApi.setColumnVisible('agListQuantityCell', true);
    columnApi.setColumnVisible('tabletCell', false);
    columnApi.setColumnVisible('mobileCell', false);

    if (screenWidth <= size.laptopL) {
      columnApi.setColumnVisible('agListReferenceCell', false);
    }
    if (screenWidth <= size.laptop) {
      columnApi.setColumnVisible('tabletCell', true);
      columnApi.setColumnVisible('agListNameCell', false);
      columnApi.setColumnVisible('agListReferenceCell', false);
      columnApi.setColumnVisible('agListPriceCell', false);
    }
    if (screenWidth <= size.tabletR) {
      columnApi.setColumnVisible('tabletCell', false);
      columnApi.setColumnVisible('agListNameCell', true);
      columnApi.setColumnVisible('agListPriceCell', true);
    }
    if (screenWidth <= size.tablet) {
      columnApi.setColumnVisible('agListPriceCell', false);
    }
    if (screenWidth <= size.mobileL) {
      columnApi.setColumnVisible('mobileCell', true);
      columnApi.setColumnVisible('tabletCell', false);
      columnApi.setColumnVisible('agListNameCell', false);
      columnApi.setColumnVisible('agListPriceCell', false);
      columnApi.setColumnVisible('agListQuantityCell', false);
      columnApi.setColumnVisible('favoriteCell', false);
    }
    gridApi.sizeColumnsToFit();
  }
};

const PromotionList = ({
  items,
  onCellClicked,
  frameworkComponents,
  handleFilter,
  currentProduct,
  currentPlatform,
  columnApi,
  screenWidth,
  selectedPlatform,
  fetchProducts,
  chooseCurrentProduct,
  fetchPromotionList,
  chooseTabPromotionList,
  promotionList,
  searchValue,
}) => {
  const [gridApi, setGridApi] = useState(null);
  // const [gridColumnApi, setGridColumnApi] = useState(null);
  const [currentTab, setCurrentTab] = useState('current');
  const [rowCount, setRowCount] = useState(0);
  const [rowData, setRowData] = useState([]);

  useEffect(() => {
    window.addEventListener('resize', resizeGrid(screenWidth, columnApi, gridApi));
  });

  useEffect(() => {
    // chooseCurrentProduct(null, selectedPlatform);
    fetchPromotionList(currentPlatform.id);
    setCurrentTab('current');
    chooseTabPromotionList('current');
  }, []);

  useEffect(() => {
    if (promotionList) {
      const currentTabPromotionList =
        currentTab === 'current' ? promotionList.current : promotionList.future;
      if (currentTabPromotionList) {
        const promotedItemId = currentTabPromotionList.reduce((accumulator, promotion) => {
          if (promotion.items) {
            return [...accumulator, ...promotion.items];
          }

          return accumulator;
        }, []);

        return setRowData(
          items
            .filter((item) => promotedItemId.includes(String(item.id)))
            .map((item) => {
              const promotion = currentTabPromotionList.find(
                (promotionItem) =>
                  promotionItem.items && promotionItem.items.includes(String(item.id))
              );
              return {
                ...item,
                promotion: {
                  ...promotion,
                  ...getPromotionFields(item),
                },
              };
            })
        );
      }
      return setRowData([]);
    }

    return null;
  }, [items, currentTab, promotionList]);

  const getRowHeight = () => {
    if (window.innerWidth <= size.mobileL) {
      return aggridConstants.LIST_MOBILE_ROW_HEIGHT;
    }

    return aggridConstants.LIST_ROW_HEIGHT;
  };

  const onColumnResized = (params) => {
    params.api.resetRowHeights();
  };

  const onColumnVisible = (params) => {
    params.api.resetRowHeights();
  };

  const onFilterChanged = () => {
    if (gridApi) {
      setRowCount(gridApi.getModel().rootNode.childrenAfterFilter.length);
      if (rowCount < 1) {
        gridApi.showNoRowsOverlay();
      }
    }
  };

  const onGridReady = (params) => {
    setGridApi(params.api);
    // setGridColumnApi(params.columnApi);
    const { attributs } = currentPlatform;
    const shouldDisplayStock = !Number(
      getValueOfAttribut(attributs || [], platformConstants.STOCK_DISPLAY_OUT_KEY)
    );

    if (!shouldDisplayStock) {
      params.columnApi.setColumnVisible('availability', false);
    }

    params.api.sizeColumnsToFit();
    window.onresize = () => {
      params.api.sizeColumnsToFit();
    };
    fetchProducts(currentPlatform.id);
    // externalFilterChanged(currentFilters);
    // sortByDefault();
    // saveSort();
  };

  const onSelectionChanged = (event) => {
    if (event.node) {
      event.node.setSelected(true);
    }
    const currentProduct = gridApi.getSelectedRows();
    chooseCurrentProduct(currentProduct[0], currentPlatform, currentTab);
  };

  const onFirstDataRendered = (params) => {
    const rowCount = gridApi.getModel().rootNode.childrenAfterFilter.length;

    params.api.sizeColumnsToFit();
    setRowCount(rowCount);
  };

  // const externalFilterChanged = (newValue) => {
  //   if (gridApi) {
  //     this.setState({ filterType: newValue }, () => {
  //       gridApi.onFilterChanged();
  //     });
  //   }
  // };

  // const isExternalFilterPresent = () => this.state.filterType !== aggridConstants.FILTER_NO_FILTER;

  // const doesExternalFilterPass = (node) => {
  //   const { filterType } = this.state;
  //   const { topSales } = this.props;
  //
  //   if (!filterType) {
  //     return true;
  //   }
  //
  //   return doFiltersPass(
  //     filterType.quickList,
  //     filterType.selection,
  //     filterType.category,
  //     filterType.availabilityList,
  //     filterType.storeAvailabilityList,
  //     filterType.temperatureList,
  //     node.data,
  //     topSales.items
  //   );
  // };

  const navigateToNextCell = (params) => {
    let previousCell = params.previousCellPosition;
    const suggestedNextCell = params.nextCellPosition;

    const KEY_UP = 38;
    const KEY_DOWN = 40;
    const KEY_LEFT = 37;
    const KEY_RIGHT = 39;

    switch (params.key) {
      case KEY_DOWN:
        previousCell = params.previousCellPosition;
        // set selected cell on current cell + 1
        gridApi.forEachNode((node) => {
          if (previousCell.rowIndex + 1 === node.rowIndex) {
            node.setSelected(true);
            chooseCurrentProduct(node.data, currentPlatform, currentTab);
          }
        });
        return suggestedNextCell;
      case KEY_UP:
        previousCell = params.previousCellPosition;
        // set selected cell on current cell - 1
        gridApi.forEachNode((node) => {
          if (previousCell.rowIndex - 1 === node.rowIndex) {
            node.setSelected(true);
            chooseCurrentProduct(node.data, currentPlatform, currentTab);
          }
        });
        return suggestedNextCell;
      case KEY_LEFT:
        return suggestedNextCell;
      case KEY_RIGHT:
        return suggestedNextCell;
      default:
        return null;
    }
  };
  return (
    <div className="promotion-list__container">
      <div className="promotion-list__sub-container">
        <AgListWrapper className="promotion-list__wrapper">
          <ul className="promotion-list__tab-container">
            <TabWrapper
              active={currentTab === 'current'}
              onClick={() => {
                setCurrentTab('current');
                chooseTabPromotionList('current');
              }}
            >
              Promotions en cours
            </TabWrapper>
            <TabWrapper
              active={currentTab === 'future'}
              onClick={() => {
                setCurrentTab('future');
                chooseTabPromotionList('future');
              }}
            >
              Promotions à venir
            </TabWrapper>
          </ul>
          <div className="AggridContainer ag-theme-custom-list">
            <AgGridReact
              columnDefs={columnDefs}
              rowData={rowData}
              rowSelection="single"
              onGridReady={onGridReady}
              onFirstDataRendered={onFirstDataRendered}
              onCellClicked={onCellClicked}
              onFilterChanged={onFilterChanged}
              localeText={localeText}
              defaultColDef={{ resizable: true }}
              frameworkComponents={frameworkComponents}
              pivotMode={false}
              tooltipShowDelay={0}
              loadingOverlayComponent="Spinner"
              onSelectionChanged={onSelectionChanged}
              // doesExternalFilterPass={doesExternalFilterPass}
              // isExternalFilterPresent={isExternalFilterPresent}
              getRowHeight={getRowHeight}
              onColumnVisible={onColumnVisible}
              onColumnResized={onColumnResized}
              suppressContextMenu
              navigateToNextCell={navigateToNextCell}
              searchValue={searchValue}
              currentTabPromotion={currentTab}
            />
          </div>
        </AgListWrapper>
        {screenWidth > size.tabletR && (
          <div className="promotion-list__product-card-container">
            <div className="promotion-list__product-card-container__border">
              {currentProduct && (
                <ProductCardWrapper className="promotion-list__product-card-wrapper">
                  <ProductCard
                    product={currentProduct}
                    close={() => {
                      chooseCurrentProduct(null);
                    }}
                    handleFilter={handleFilter}
                    searchValue={searchValue}
                    source="promotionList"
                    currentList={currentTab}
                  />
                </ProductCardWrapper>
              )}
              {!currentProduct && (
                <div className="product-card__no-product">Aucun article séléctionné</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  items: selectItemsFromCatalog(state, { aggrid: true }),
  screenWidth: selectScreenWidth(state),
  currentPlatform: selectCurrentPlatform(state),
  promotionList: selectPromotionData(state),
  currentProduct: selectPromotionSelectedProduct(state),
  currentPromotionTab: selectPromotionsCurrentTab(state),
  pageContext: selectCurrentPageContext(state),
});

const mapDispatchToProps = (dispatch) => ({
  fetchProducts: (platformId, categoryIds = []) =>
    dispatch(productActions.fetchAllProducts(platformId, categoryIds, 4200)),
  fetchPromotionList: (platformId) => dispatch(promotionActions.fetchPromotionList(platformId)),
  chooseTabPromotionList: (currentTab) => {
    const currentlist = currentTab.charAt(0).toUpperCase() + currentTab.slice(1);
    dispatch(promotionActions.chooseTabPromotionList(currentlist));
  },
  chooseCurrentProduct: (product, platform, currentTab) => {
    if (product !== null) {
      let source = '';
      if (currentTab === 'current') {
        source = 'PROMOTION_CURRENT';
      } else {
        source = 'PROMOTION_FUTURE';
      }
      analytics.productDetails(product, source);
    }
    dispatch(promotionActions.chooseCurrentProduct(product, platform));
  },
  pageContext: dispatch(pageContextActions.selectPageContext('PAGE_PROMOTION')),
});

PromotionList.propTypes = {
  frameworkComponents: PropTypes.object,
  handleFilter: PropTypes.func,
  currentProduct: PropTypes.object,
  screenWidth: PropTypes.number,
  searchValue: PropTypes.string,
};

export default connect(mapStateToProps, mapDispatchToProps)(PromotionList);
